import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_OTHER_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
  PENDING_STATUS,
} from 'contexts/preferences/listing-params';
import { LOCALE_SEARCH_PATHS, SiteLanguage } from 'types/locales';
import { formatI18nDate } from 'utils/i18n-format-date';
import { formatI18nCompactPrice } from 'utils/i18n-format-currency';
import { PartialDeep } from 'type-fest';
import { capitalize } from '@zoocasa/node-kit/strings/capitalize';
import {
  FilterItemData,
  ListingFilterBarType,
  PriceRangeFilter,
  BedroomsFilter,
  BathroomsFilter,
  PropertyTypeFilterItem,
  StatusFilter,
  RentalFilter,
  SqftFilter,
  MiscFilter,
  ParkingSpacesFilter,
  HasImageFilter,
  LockerFilters,
  MaintenanceFeeFilters,
  ListedSinceFilter,
  ListedToFilter,
} from './listing_filter_bar';

export interface FilterConfig {
  label: string;
  render: (filter: FilterItemData) => React.ReactNode;
  removeAction: (
    onValueChange: (filters: PartialDeep<ListingFilterBarType>) => void,
  ) => void;
}

export const createFilterConfigs = (
  t: (
    key: string,
    options?: { defaultValue?: string; [key: string]: any },
  ) => string,
  locale: SiteLanguage = SiteLanguage.EnglishCA
): {[K in FilterItemData['type']]: FilterConfig } => {

  const formatPrice = (value: number) =>
    formatI18nCompactPrice(value, locale);

  return {
    priceRange: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_PRICE_RANGE, {
        defaultValue: 'Price Range',
      }),
      render: filter => {
        const { min, max } = filter as PriceRangeFilter;
        if (min === null && max === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ANY_PRICE, {
            defaultValue: 'Any Price',
          });
        if (min === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_UP_TO_PRICE, {
            defaultValue: `Up to ${formatPrice(max!)}`,
            price: formatPrice(max!),
          });
        if (max === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FROM_PRICE, {
            defaultValue: `From ${formatPrice(min)}+`,
            price: formatPrice(min),
          });
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_PRICE_RANGE_FORMAT, {
          defaultValue: `${formatPrice(min)} - ${formatPrice(max)}`,
          min: formatPrice(min),
          max: formatPrice(max),
        });
      },
      removeAction: onValueChange =>
        onValueChange({ priceMin: null, priceMax: null }),
    },
    bedrooms: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BEDROOMS, {
        defaultValue: 'Bedrooms',
      }),
      render: filter => {
        const { bedrooms } = filter as BedroomsFilter;
        return bedrooms === '0+'
          ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_ANY_BEDS, {
            defaultValue: 'Any Beds',
          })
          : t(LOCALE_SEARCH_PATHS.FILTER_CHIP_BEDS_FORMAT, {
            defaultValue: `${bedrooms} Beds`,
            bedrooms,
          });
      },
      removeAction: onValueChange => onValueChange({ bedrooms: '0+' }),
    },
    bathrooms: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BATHROOMS, {
        defaultValue: 'Bathrooms',
      }),
      render: filter => {
        const { bathrooms } = filter as BathroomsFilter;
        return bathrooms === '0+'
          ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_ANY_BATHS, {
            defaultValue: 'Any Baths',
          })
          : t(LOCALE_SEARCH_PATHS.FILTER_CHIP_BATHS_FORMAT, {
            defaultValue: `${bathrooms} Baths`,
            bathrooms,
          });
      },
      removeAction: onValueChange => onValueChange({ bathrooms: '0+' }),
    },
    propertyType: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_LABELS_HOME_TYPE, {
        defaultValue: 'Home Type',
      }),
      render: filter => {
        const { homeType } = filter as PropertyTypeFilterItem;
        const selectedTypes = Object.keys(homeType).filter(
          key => homeType[key]
        );

        if (selectedTypes.length === 0 || selectedTypes.length === 6) {
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_ANY_HOME_TYPE, {
            defaultValue: 'Any Home Type',
          });
        }

        // Format each selected type with localization
        const formattedTypes = selectedTypes.map(type => {
          const typeKey =
            `FILTER_PROPERTY_TYPES_${type.toUpperCase()}` as keyof typeof LOCALE_SEARCH_PATHS;
          return t(
            LOCALE_SEARCH_PATHS[typeKey] ||
              `search:filter.property_types.${type}`,
            {
              defaultValue: capitalize(type.toLowerCase().replace(/_/g, ' ')),
            }
          );
        });

        // Join with commas, but limit to first 2 types to avoid overflow
        if (formattedTypes.length <= 2) {
          return formattedTypes.join(', ');
        } else {
          const firstTwo = formattedTypes.slice(0, 2).join(', ');
          const remainingCount = formattedTypes.length - 2;
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_HOME_TYPES_PLUS_COUNT, {
            defaultValue: `${firstTwo} + ${remainingCount}`,
            types: firstTwo,
            count: remainingCount,
          });
        }
      },
      removeAction: onValueChange =>
        onValueChange({
          homeType: {
            commercial: true,
            condo: true,
            farm: true,
            house: true,
            land: true,
            townhouse: true,
          },
        }),
    },
    status: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTING_STATUS, {
        defaultValue: 'Listing Status',
      }),
      render: filter => {
        const { status } = filter as StatusFilter;
        switch (status) {
          case NOT_AVAILABLE_SOLD_STATUS:
            return t(LOCALE_SEARCH_PATHS.FILTER_STATUS_SOLD, {
              defaultValue: 'Sold',
              suffix: '',
            });
          case NOT_AVAILABLE_OTHER_STATUS:
            return t(LOCALE_SEARCH_PATHS.FILTER_STATUS_EXPIRED, {
              defaultValue: 'Expired',
              suffix: '',
            });
          case AVAILABLE_STATUS:
          default:
            return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FOR_SALE, {
              defaultValue: 'For Sale',
            });
          case PENDING_STATUS:
            return t(LOCALE_SEARCH_PATHS.FILTER_STATUS_PENDING, {
              defaultValue: 'Pending',
              suffix: '',
            });
        }
      },
      removeAction: onValueChange =>
        onValueChange({ status: AVAILABLE_STATUS }),
    },
    rental: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTING_STATUS, {
        defaultValue: 'Listing Type',
      }),
      render: filter => {
        const { rental } = filter as RentalFilter;
        return rental
          ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FOR_RENT, {
            defaultValue: 'For Rent',
          })
          : t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FOR_SALE, {
            defaultValue: 'For Sale',
          });
      },
      removeAction: onValueChange => onValueChange({ rental: false }),
    },
    sqft: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_SQFT, { defaultValue: 'Sqft' }),
      render: filter => {
        const { sqftMin, sqftMax } = filter as SqftFilter;
        if (sqftMin === null && sqftMax === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_ANY_SQFT_CHIP, {
            defaultValue: 'Any Sqft',
          });
        if (sqftMin === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_UP_TO_SQFT, {
            defaultValue: `Up to ${sqftMax} Sqft`,
            sqft: sqftMax,
          });
        if (sqftMax === null)
          return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FROM_SQFT, {
            defaultValue: `From ${sqftMin} Sqft+`,
            sqft: sqftMin,
          });
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_SQFT_RANGE_FORMAT, {
          defaultValue: `${sqftMin} - ${sqftMax} Sqft`,
          min: sqftMin,
          max: sqftMax,
        });
      },
      removeAction: onValueChange =>
        onValueChange({ sqftMin: null, sqftMax: null }),
    },
    parkingSpaces: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_PARKING, {
        defaultValue: 'Parking',
      }),
      render: filter => {
        const { parkingSpaces } = filter as ParkingSpacesFilter;
        return (
          parkingSpaces &&
          t(LOCALE_SEARCH_PATHS.FILTER_CHIP_PARKING_FORMAT, {
            defaultValue: `${parkingSpaces} Parking`,
            spaces: parkingSpaces,
          })
        );
      },
      removeAction: onValueChange => onValueChange({ parkingSpaces: '0+' }),
    },
    hasImage: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_HAS_IMAGE, {
        defaultValue: 'Has Image',
      }),
      render: filter => {
        const { hasImage } = filter as HasImageFilter;
        return hasImage
          ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_HAS_IMAGE, {
            defaultValue: 'Has Image',
          })
          : t(LOCALE_SEARCH_PATHS.FILTER_CHIP_SHOW_LISTINGS_NO_IMAGES, {
            defaultValue: 'Show Listings With No Images',
          });
      },
      removeAction: onValueChange => onValueChange({ hasImage: true }),
    },
    listedSince: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTED_SINCE, {
        defaultValue: 'Listed Since',
      }),
      render: filter => {
        const { listedSince } = filter as ListedSinceFilter;
        if (!listedSince) return null;
        const formattedDate = formatI18nDate(
          new Date(listedSince),
          'short',
          locale
        );
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTED_SINCE_FORMAT, {
          defaultValue: `Listed Since ${formattedDate}`,
          date: formattedDate,
        });
      },
      removeAction: onValueChange => onValueChange({ listedSince: null }),
    },
    listedTo: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTED_TO, {
        defaultValue: 'Listed To',
      }),
      render: filter => {
        const { listedTo } = filter as ListedToFilter;
        if (!listedTo) return null;
        const formattedDate = formatI18nDate(
          new Date(listedTo),
          'short',
          locale
        );
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LISTED_TO_FORMAT, {
          defaultValue: `Listed To ${formattedDate}`,
          date: formattedDate,
        });
      },
      removeAction: onValueChange => onValueChange({ listedTo: null }),
    },
    misc: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_MISC, { defaultValue: 'Misc' }),
      render: filter => {
        const { garage, openHouse, pool, waterfront, fireplace } =
          filter as MiscFilter;
        const filters = [];
        if (garage)
          filters.push(
            t(LOCALE_SEARCH_PATHS.FILTER_CHIP_GARAGE, {
              defaultValue: 'Garage',
            })
          );
        if (openHouse)
          filters.push(
            t(LOCALE_SEARCH_PATHS.FILTER_CHIP_OPEN_HOUSE, {
              defaultValue: 'Open House',
            })
          );
        if (pool)
          filters.push(
            t(LOCALE_SEARCH_PATHS.FILTER_CHIP_POOL, { defaultValue: 'Pool' })
          );
        if (waterfront)
          filters.push(
            t(LOCALE_SEARCH_PATHS.FILTER_CHIP_WATERFRONT, {
              defaultValue: 'Waterfront',
            })
          );
        if (fireplace)
          filters.push(
            t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FIREPLACE, {
              defaultValue: 'Fireplace',
            })
          );
        const formattedFilters = filters.slice(0, 2).join(', ');
        // Only show the first 2 filters
        return filters.length > 2
          ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_MISC_PLUS_COUNT, {
            defaultValue: `${formattedFilters} + ${filters.length - 2}`,
            filters: formattedFilters,
            count: filters.length - 2,
          })
          : formattedFilters;
      },
      removeAction: onValueChange =>
        onValueChange({
          garage: false,
          openHouse: false,
          pool: false,
          waterfront: false,
          fireplace: false,
        }),
    },
    locker: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LOCKER, {
        defaultValue: 'Locker',
      }),
      render: filter => {
        const { locker } = filter as LockerFilters;
        if (locker) {
          return locker === 'yes'
            ? t(LOCALE_SEARCH_PATHS.FILTER_CHIP_HAS_LOCKER, {
              defaultValue: 'Has Locker',
            })
            : t(LOCALE_SEARCH_PATHS.FILTER_CHIP_NO_LOCKER, {
              defaultValue: 'No Locker',
            });
        }
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LOCKER_FILTER, {
          defaultValue: 'Locker Filter',
        });
      },
      removeAction: onValueChange =>
        onValueChange({
          additional: {
            condoOrTownhouse: {
              locker: 'any',
            },
          },
        }),
    },
    maintenanceFee: {
      label: t(LOCALE_SEARCH_PATHS.FILTER_CHIP_MAINTENANCE_FEE, {
        defaultValue: 'Maintenance Fee',
      }),
      render: filter => {
        const { maintenanceFee } = filter as MaintenanceFeeFilters;
        const formattedFee = maintenanceFee ? formatPrice(maintenanceFee) : '';
        return t(LOCALE_SEARCH_PATHS.FILTER_CHIP_MAX_MAINTENANCE_FEE_FORMAT, {
          defaultValue: `Max ${formattedFee} Maintenance Fee`,
          fee: formattedFee,
        });
      },
      removeAction: onValueChange =>
        onValueChange({
          additional: {
            condoOrTownhouse: {
              maintenanceFee: null,
            },
          },
        }),
    },
  };
};
